/* eslint-disable @next/next/no-img-element */
import { useEffect, useRef, useState } from "react"
import { Section, Spacer, Subtitle, Title } from "../misc"
import { SignUpButton } from "../Main"
import { ResponsiveScale } from "../ResponsiveScale"
import tw from "tailwind-styled-components"
import Hero from "public/marketing/hero.png"
import { useScrollAnimation } from "./Customers"
import useAnalytics from "app/analytics/hooks/useAnalytics"
import { Post } from "integrations/contentful"
import Link from "next/link"
import { SIcon, SIconType, SStatefullAutoModal } from "@superwall-me/sui"
import moment from "moment"

const Laptop = tw.div`
flex flex-col gap-2 w-full items-center justify-center
w-[1400px]
`

const LaptopTop = tw.div`
relative
flex
flex-col
items-center
border
border-2
border-white
border-opacity-5
rounded-3xl
p-3
bg-white
bg-opacity-5
rounded-b-xl
w-[88%]
rotate-y-10
shadow-2xl
`

const LaptopBottom = tw.div`
  relative
  flex
  flex-col
  items-center
  bg-opacity-5
  bg-white
  border
  border-2
  border-opacity-5
  border-white
  h-10
  rounded-b-3xl
  rounded-xl
  shadow-2xl
  w-full
`

const Badge = tw.div`
  flex-row items-center justify-center gap-3
  bg-brand-primary bg-opacity-7 text-brand-primary
  mb-8
  px-2 py-2
  rounded-xl
  text-sm
  md:text-base
  flex
  md:min-h-[45px]
  rounded-[23px]
  border border-1 border-brand-primary border-opacity-10
 `

export const FeaturedPost = ({ post }: { post: Post }) => {
  return (
    (<Link href={`/blog/${post.slug}`}>

      <div
        className={
          moment().diff(post.createdAt, "hours") < 24 * 14 ? "" : "opacity-0 pointer-events-none"
        }
      >
        <Badge>
          <div className="flex flex-row gap-1 md:gap-1 items-center justify-center">
            <SIcon className="inline mr-2 ml-2" type="megaphone" size={16}></SIcon>
            <div className="w-auto max-w-[65vw]  md:max-w-2xl inline-block truncate">
              {post.title}
            </div>

            <SIcon className="inline" type={"chevron-right"} size={24}></SIcon>
          </div>
        </Badge>
      </div>

    </Link>)
  );
}

const useScrollOffset = () => {
  const [scrollY, setScrollY] = useState(0)
  const [bodyHeight, setBodyHeight] = useState(0)

  const mainId = "sw-main"

  useEffect(() => {
    if (typeof window === "undefined") {
      return
    }

    const handleScroll = () => {
      setScrollY(window.scrollY)
    }

    setBodyHeight(document.getElementById(mainId)?.clientHeight || 0)

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  if (typeof window === "undefined") {
    return { scrollY: 0, percentage: 0 }
  }

  return { scrollY, percentage: scrollY / (bodyHeight - window.innerHeight) }
}

export const Header = ({ post }: { post?: Post }) => {
  const [loaded, setLoaded] = useState(false)

  // on mount

  useEffect(() => {
    setLoaded(true)
    return () => {
      // on unmount
      setLoaded(false)
    }
  }, [])

  function openYoutubeVideo() {
    // Dimensions of the new window
    const width = 1000
    const height = 600

    // Calculating the center position
    const left = window.screen.width / 2 - width / 2
    const top = window.screen.height / 2 - height / 2

    // Parameters for the new window
    const params = `width=${width}, height=${height}, top=${top}, left=${left}, resizable=yes, scrollbars=yes, status=yes`

    // Open the new window
    window.open(
      "https://www.youtube.com/embed/c58Ja2bNuH0?si=SY42OIUpf9nyNX4v",
      "NewWindow",
      params
    )
  }

  // on scroll, paralax the stars
  const { scrollY, percentage } = useScrollOffset()

  return (
    <Section
      // onClick={() => {
      //   setLoaded(!loaded)
      // }}
      className="text-center"
    >
      <div
        style={{
          transform: `translateY(-${scrollY / (4.0 / (1 - percentage + 1))}px)`,
          perspectiveOrigin: `50% ${scrollY / 4}px`,
          opacity: 1 - percentage,
          zIndex: 0,
          top: "20vh",
        }}
        className="sw-starfield-container fixed"
      >
        <div className="sw-starfield"></div>
      </div>
      {post && <FeaturedPost post={post} />}
      <Title
        style={{
          letterSpacing: "-0.02em",
        }}
      >
        Quickly build & test paywalls without shipping updates{" "}
      </Title>
      <Subtitle>
        Stop wrestling with your codebase to build paywalls. Start building & deploying paywalls in
        10 minutes flat.
      </Subtitle>
      <Spacer></Spacer>
      <div className="flex flex-row gap-10 items-center">
        <SignUpButton
          source="main-cta"
          mainCta="Start for free"
          bookDemoCta={"Talk to sales"}
          showBookDemo={true}
        ></SignUpButton>
      </div>

      <Spacer></Spacer>
      <div></div>
      <ResponsiveScale loaded={loaded}>
        <Laptop
          style={{
            transition: "all 2s ease",
            opacity: loaded ? "1" : "0",
            transform: loaded ? "translateY(0rem) scale(1)" : "translateY(-75%) scale(0.7)",
          }}
        >
          <LaptopTop
            style={{
              transition: "all 2s ease",
              transform: loaded
                ? "perspective(375rem) rotateX(0deg)"
                : "perspective(375rem) rotateX(-90deg)",
              transformOrigin: "bottom",
            }}
          >
            <SStatefullAutoModal
              button={({ open }) => {
                return (
                  <div onClick={open} className="relative cursor-pointer group">
                    <img
                      style={{
                        transition: "all 2s ease",
                        opacity: loaded ? "1" : "0",
                      }}
                      className="border border-[2px] border-white border-opacity-5 w-full rounded-xl"
                      src={Hero.src}
                      alt="dashboard-header"
                    ></img>
                    <div className="flex flex-row items-center justify-center absolute left-[50%] top-[50%] -translate-x-[50%] -translate-y-[50%] group-hover:scale-[0.95] group-active:scale-[0.9] transition rounded-full bg-brand-primary/10 backdrop-blur-lg border border-brand-primary/10 w-[150px] h-[150px]">
                      <span className="">
                        <SIcon
                          type={SIconType.PlayLarge}
                          size={64}
                          className="translate-x-[5px] text-brand-primary"
                        ></SIcon>
                      </span>
                    </div>
                  </div>
                )
              }}
              title={undefined}
              initialState={undefined}
              // children={<p>test</p>}
              actions={[
                {
                  title: "Done",
                  onClick: () => {},
                },
              ]}
            >
              <div className="w-[80vw] max-w-[1000px]">
                <iframe
                  // width="560"
                  style={{
                    transition: "all 2s ease",
                    opacity: loaded ? "1" : "0",
                    aspectRatio: "16/9",
                  }}
                  className="border border-[2px] border-white border-opacity-5 w-full rounded-xl"
                  // height="315"
                  src="https://www.youtube.com/embed/c58Ja2bNuH0?si=SY42OIUpf9nyNX4v?autoplay=1"
                  title="YouTube video player"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              </div>
            </SStatefullAutoModal>
          </LaptopTop>

          <LaptopBottom
            style={{
              transition: "all 2s ease",
              transform: loaded
                ? "perspective(375rem) rotateX(0deg)"
                : "perspective(375rem) rotateX(90deg)",
              transformOrigin: "top",
            }}
          >
            <div className="rounded-b-full bg-white shadow-inner bg-opacity-5 w-[17%] h-3 absolute  top-0"></div>
          </LaptopBottom>
        </Laptop>
      </ResponsiveScale>
    </Section>
  )
}
